import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useOffline } from '../contexts/OfflineContext';
import { Minus, Plus, ChevronRight, ChevronLeft, Check, Package, Pencil } from 'lucide-react';
import { CONVERSION } from '../constants/productos';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function InventarioInicial() {
  const { user } = useAuth();
  const { isOnline, addOfflineItem } = useOffline();
  const navigate = useNavigate();

  const [quantities, setQuantities] = useState({});
  const [pollosEnteros, setPollosEnteros] = useState('');
  const [hasInicial, setHasInicial] = useState(false);
  const [existingData, setExistingData] = useState(null);
  const [asignacion, setAsignacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editQuantities, setEditQuantities] = useState({});

  const isPropietario = user?.role === 'propietario';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    if (!user?.punto_venta_id) {
      setLoading(false);
      return;
    }
    try {
      const { data: invData } = await axios.get(`${API}/inventario/actual/${user.punto_venta_id}`);
      if (invData.has_inicial) {
        setHasInicial(true);
        const inicialEfectivo = invData.productos_iniciales || invData.productos || {};
        setExistingData(inicialEfectivo);
        setLoading(false);
        return;
      }

      const { data: asigData } = await axios.get(`${API}/asignaciones/${user.punto_venta_id}`);
      if (asigData && asigData.pollos_enteros !== undefined) {
        setAsignacion(asigData);
        const pollos = asigData.pollos_enteros;
        setPollosEnteros(String(pollos));
        const piezas = {};
        for (const [clave, info] of Object.entries(CONVERSION)) {
          piezas[clave] = pollos * info.factor;
        }
        setQuantities(piezas);
        if (asigData.status === 'confirmada') {
          setHasInicial(true);
          setExistingData(asigData.productos_recibidos || {});
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePollosEnteros = (valor) => {
    const pollos = parseInt(valor, 10);
    setPollosEnteros(valor);
    if (!isNaN(pollos) && pollos >= 0) {
      const piezas = {};
      for (const [clave, info] of Object.entries(CONVERSION)) {
        piezas[clave] = pollos * info.factor;
      }
      setQuantities(piezas);
    }
  };

  const handleIncrement = (clave) => {
    if (hasInicial) return;
    setQuantities(prev => ({ ...prev, [clave]: (prev[clave] || 0) + 1 }));
  };

  const handleDecrement = (clave) => {
    if (hasInicial) return;
    setQuantities(prev => ({ ...prev, [clave]: Math.max(0, (prev[clave] || 0) - 1) }));
  };

  const getTotalPiezas = () => {
    const data = hasInicial ? existingData : quantities;
    return Object.values(data || {}).reduce((sum, qty) => sum + (qty || 0), 0);
  };

  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);
    const productos = { ...quantities };

    try {
      if (asignacion && asignacion.status === 'pendiente') {
        await axios.put(`${API}/asignaciones/${asignacion.id}/confirmar`, {
          productos_recibidos: productos,
        });
      } else {
        await axios.post(`${API}/inventario/inicial`, {
          punto_venta_id: user.punto_venta_id,
          productos,
        });
      }
      navigate('/pos');
    } catch (err) {
      if (!isOnline) {
        addOfflineItem('inventarios', {
          punto_venta_id: user.punto_venta_id,
          productos,
          fecha: new Date().toISOString().split('T')[0],
        });
        navigate('/pos');
      } else {
        setError(err.response?.data?.detail || 'Error al registrar inventario');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEdit = () => {
    setEditQuantities({ ...existingData });
    setEditMode(true);
    setError('');
  };

  const handleAjuste = async () => {
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/inventario/inicial/ajuste`, {
        punto_venta_id: user.punto_venta_id,
        productos: editQuantities,
      });
      setExistingData({ ...editQuantities });
      setEditMode(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar ajuste');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="inline-block w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  const displayData = editMode ? editQuantities : (hasInicial ? existingData : quantities);
  const isReadOnly = hasInicial && !editMode;

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="inventario-inicial-page">
      {/* Header */}
      <div className="px-6 py-4 border-b border-zinc-200">
        <div className="flex items-center gap-4">
          <button
            onClick={() => editMode ? setEditMode(false) : navigate(-1)}
            className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg"
            data-testid="btn-back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-zinc-900">
            {editMode ? 'Corregir inventario inicial' : 'Inventario inicial'}
          </h1>
          {hasInicial && !editMode && isPropietario && (
            <button
              onClick={handleOpenEdit}
              className="ml-auto flex items-center gap-2 px-3 py-2 border border-zinc-300 rounded-lg text-sm font-medium text-zinc-700 active:bg-zinc-100"
              data-testid="btn-corregir"
            >
              <Pencil className="w-4 h-4" />
              Corregir
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 py-4">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Assignment banner */}
        {asignacion && !hasInicial && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="banner-asignacion">
            <div className="flex items-center gap-3 mb-1">
              <Package className="w-5 h-5 text-amber-600" />
              <p className="font-bold text-amber-800">
                Asignacion: {asignacion.pollos_enteros} pollos enteros
              </p>
            </div>
            <p className="text-sm text-amber-700">Puedes ajustar la cantidad si recibiste diferente.</p>
          </div>
        )}

        {/* Pollos enteros */}
        {!hasInicial && (
          <div className="mb-4 p-4 bg-zinc-50 border border-zinc-200 rounded-xl">
            <p className="text-sm font-medium text-zinc-700 mb-3">Captura rápida por pollos enteros</p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handlePollosEnteros(String(Math.max(0, (parseInt(pollosEnteros) || 0) - 1)))}
                disabled={!pollosEnteros || parseInt(pollosEnteros) <= 0}
                className="w-12 h-12 flex items-center justify-center border border-zinc-300 rounded-lg active:bg-zinc-100 disabled:opacity-30"
              >
                <Minus className="w-5 h-5" />
              </button>
              <input
                type="number"
                inputMode="numeric"
                min="0"
                value={pollosEnteros}
                onChange={(e) => handlePollosEnteros(e.target.value)}
                placeholder="0"
                className="flex-1 h-12 text-center text-2xl font-bold border border-zinc-300 rounded-lg focus:outline-none focus:border-zinc-600"
              />
              <button
                onClick={() => handlePollosEnteros(String((parseInt(pollosEnteros) || 0) + 1))}
                className="w-12 h-12 flex items-center justify-center border border-zinc-300 rounded-lg active:bg-zinc-100"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-zinc-400 mt-2">Las piezas se calculan automáticamente. Puedes ajustar individualmente abajo.</p>
          </div>
        )}

        {/* Product List */}
        <div className="mb-4">
          <p className="text-sm font-medium text-zinc-600 mb-2">
            {editMode ? 'Corrige las cantidades' : hasInicial ? 'Inventario registrado' : 'Desglose de piezas'}
          </p>
          <div className="space-y-1">
            {Object.entries(CONVERSION).map(([clave, info]) => {
              const qty = displayData?.[clave] || 0;
              return (
                <div
                  key={clave}
                  className="flex items-center justify-between py-4 border-b border-zinc-100"
                  data-testid={`producto-${clave}`}
                >
                  <span className="text-lg font-medium text-zinc-900">{info.nombre}</span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        if (editMode) {
                          setEditQuantities(prev => ({ ...prev, [clave]: Math.max(0, (prev[clave] || 0) - 1) }));
                        } else {
                          handleDecrement(clave);
                        }
                      }}
                      disabled={isReadOnly || qty === 0}
                      className="w-12 h-12 flex items-center justify-center border border-zinc-300 rounded-lg text-zinc-600 active:bg-zinc-100 disabled:opacity-30"
                      data-testid={`btn-minus-${clave}`}
                    >
                      <Minus className="w-5 h-5" />
                    </button>
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      value={qty === 0 ? '' : qty}
                      onChange={(e) => {
                        if (isReadOnly) return;
                        const val = parseInt(e.target.value, 10);
                        const newVal = isNaN(val) || val < 0 ? 0 : val;
                        if (editMode) {
                          setEditQuantities(prev => ({ ...prev, [clave]: newVal }));
                        } else {
                          setQuantities(prev => ({ ...prev, [clave]: newVal }));
                        }
                      }}
                      onBlur={(e) => {
                        if (e.target.value === '') {
                          if (editMode) {
                            setEditQuantities(prev => ({ ...prev, [clave]: 0 }));
                          } else {
                            setQuantities(prev => ({ ...prev, [clave]: 0 }));
                          }
                        }
                      }}
                      disabled={isReadOnly}
                      placeholder="0"
                      className="w-14 text-center text-2xl font-bold border border-zinc-300 rounded-lg h-12 focus:outline-none focus:border-zinc-600 disabled:border-0 disabled:bg-transparent text-zinc-900"
                    />
                    <button
                      onClick={() => {
                        if (editMode) {
                          setEditQuantities(prev => ({ ...prev, [clave]: (prev[clave] || 0) + 1 }));
                        } else {
                          handleIncrement(clave);
                        }
                      }}
                      disabled={isReadOnly}
                      className="w-12 h-12 flex items-center justify-center border border-zinc-300 rounded-lg text-zinc-600 active:bg-zinc-100 disabled:opacity-30"
                      data-testid={`btn-plus-${clave}`}
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Total */}
        <div className="flex items-center justify-between py-4 mt-2">
          <span className="text-lg font-bold text-zinc-900">Total piezas</span>
          <span className="text-2xl font-bold text-zinc-900">{getTotalPiezas()}</span>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 pb-8 pt-4 border-t border-zinc-200">
        {editMode ? (
          <div className="flex gap-3">
            <button
              onClick={() => setEditMode(false)}
              disabled={submitting}
              className="flex-1 h-14 border border-zinc-300 rounded-xl text-lg font-medium text-zinc-700 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleAjuste}
              disabled={submitting}
              className="flex-1 h-14 bg-zinc-900 text-white rounded-xl text-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="btn-guardar-ajuste"
            >
              {submitting ? (
                <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Guardar corrección
                </>
              )}
            </button>
          </div>
        ) : hasInicial ? (
          <button
            onClick={() => navigate('/pos')}
            className="w-full h-14 bg-zinc-900 text-white rounded-xl text-lg font-medium flex items-center justify-center gap-2"
            data-testid="btn-ir-pos"
          >
            Ir al punto de venta
            <ChevronRight className="w-5 h-5" />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full h-14 bg-zinc-900 text-white rounded-xl text-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="btn-confirmar-inventario"
          >
            {submitting ? (
              <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Check className="w-5 h-5" />
                {asignacion ? 'Confirmar recepcion' : 'Registrar inventario'}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
